export type DiffChunk =
  | { type: "equal"; text: string }
  | { type: "del"; text: string }
  | { type: "ins"; text: string };

// Token boundary: run of whitespace, run of word chars, or single non-word char.
// Keeps punctuation as separate tokens so "привет!" → ["привет", "!"] diffs cleanly.
function tokenize(s: string): string[] {
  const out: string[] = [];
  const re = /\s+|[\p{L}\p{N}_]+|[^\s\p{L}\p{N}_]/gu;
  for (const m of s.matchAll(re)) out.push(m[0]);
  return out;
}

// Classic LCS diff. Memory is O(m*n); fine for chapter-length text (up to ~2000
// tokens either side ≈ 16MB). For lendings we typically stay far below that.
export function wordDiff(before: string, after: string): DiffChunk[] {
  const a = tokenize(before);
  const b = tokenize(after);
  const m = a.length;
  const n = b.length;

  const dp: Uint32Array[] = Array.from({ length: m + 1 }, () => new Uint32Array(n + 1));
  for (let i = m - 1; i >= 0; i--) {
    const row = dp[i]!;
    const next = dp[i + 1]!;
    for (let j = n - 1; j >= 0; j--) {
      if (a[i] === b[j]) row[j] = next[j + 1]! + 1;
      else row[j] = Math.max(next[j]!, row[j + 1]!);
    }
  }

  const chunks: DiffChunk[] = [];
  const push = (type: DiffChunk["type"], text: string) => {
    const last = chunks[chunks.length - 1];
    if (last && last.type === type) last.text += text;
    else chunks.push({ type, text });
  };

  let i = 0;
  let j = 0;
  while (i < m && j < n) {
    const ai = a[i]!;
    const bj = b[j]!;
    if (ai === bj) {
      push("equal", ai);
      i++;
      j++;
    } else if (dp[i + 1]![j]! >= dp[i]![j + 1]!) {
      push("del", ai);
      i++;
    } else {
      push("ins", bj);
      j++;
    }
  }
  while (i < m) push("del", a[i++]!);
  while (j < n) push("ins", b[j++]!);
  return chunks;
}

export function hasChanges(chunks: DiffChunk[]): boolean {
  return chunks.some((c) => c.type !== "equal");
}
